import { Box } from '@mui/material';

import { ActiveCampaignsSection } from '@sh4res/bo-common/components/B2bCompanyDashboard/ActiveCampaignsSection';
import { AffiliatesSection } from '@sh4res/bo-common/components/B2bCompanyDashboard/AffiliatesSection';
import { DocumentsSection } from '@sh4res/bo-common/components/B2bCompanyDashboard/DocumentsSection';
import { MatchingContributionBreakdownSection } from '@sh4res/bo-common/components/B2bCompanyDashboard/MatchingContributionBreakdownGraphs';
import { SavingsSection } from '@sh4res/bo-common/components/B2bCompanyDashboard/SavingsSection';
import { B2bCompanyDashboardProps } from '@sh4res/bo-common/components/B2bCompanyDashboard/utils';
import { useCanAccess } from '@sh4res/bo-common/hooks/useCanAccess';

export const useB2bCompanyDashboardPermissions = (
  resource: B2bCompanyDashboardProps['resource'],
) => {
  const canAccessActiveCampaigns = useCanAccess({
    resource: `${resource}/dashboard/active_campaigns`,
    action: 'show',
  });

  const canAccessSavings = useCanAccess({
    resource: `${resource}/dashboard/savings`,
    action: 'show',
  });

  const canAccessMatchingContributions = useCanAccess({
    resource: `${resource}/dashboard/matching_contributions`,
    action: 'show',
  });

  const canAccessAffiliates = useCanAccess({
    resource: `${resource}/dashboard/affiliates`,
    action: 'show',
  });

  const canAccessDocuments = useCanAccess({
    resource: `${resource}/documents`,
    action: 'list',
  });

  const hasAnyDashboardAccess =
    canAccessActiveCampaigns ||
    canAccessSavings ||
    canAccessMatchingContributions ||
    canAccessAffiliates ||
    canAccessDocuments;

  return {
    canAccessActiveCampaigns,
    canAccessSavings,
    canAccessMatchingContributions,
    canAccessAffiliates,
    canAccessDocuments,
    hasAnyDashboardAccess,
  };
};

export const B2bCompanyDashboard = ({
  useMock = false,
  resource = 'b2b/companies',
}: B2bCompanyDashboardProps) => {
  const {
    canAccessActiveCampaigns,
    canAccessSavings,
    canAccessMatchingContributions,
    canAccessAffiliates,
    canAccessDocuments,
    hasAnyDashboardAccess,
  } = useB2bCompanyDashboardPermissions(resource);

  if (!hasAnyDashboardAccess) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {canAccessActiveCampaigns && <ActiveCampaignsSection useMock={useMock} resource={resource} />}
      {canAccessSavings && <SavingsSection useMock={useMock} resource={resource} />}
      {canAccessDocuments && <DocumentsSection useMock={useMock} resource={resource} />}
      {canAccessMatchingContributions && (
        <MatchingContributionBreakdownSection useMock={useMock} resource={resource} />
      )}
      {canAccessAffiliates && <AffiliatesSection useMock={useMock} resource={resource} />}
    </Box>
  );
};
